/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { matchPath } from 'react-router';
import { isDevelopmentEnvironment, isStagingEnvironment } from 'src/core/utils/api/environment';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export type Portal = {
    kind: 'languageStudio' | 'luis' | 'demos';
    subPath?:
        | 'root'
        | 'luis'
        | 'customText'
        | 'clu'
        | 'orchestration'
        | 'qnaMaker'
        | 'translation'
        | 'summarization'
        | 'customHealthcare'
        | 'customSentiment';
    serviceName?:
        | 'classification'
        | 'ner'
        | 'root'
        | 'luis'
        | 'customText'
        | 'clu'
        | 'orchestration'
        | 'qnaMaker'
        | 'translation'
        | 'summarization'
        | 'customHealthcare'
        | 'customSentiment';
};

export type PortalEnvironmentDomainName = 'staging' | 'production' | 'dialogice3' | 'dialogice4' | 'dialogice6' | 'dialogice14';

export const openUrlInNewTab = (url: string) => window.open(url, '_blank').focus();

export const doesRouteMatchLocation = (routeToMatch: string, config?: { exact?: boolean; locationToMatch?: string }) => {
    const defaults = { exact: false, locationToMatch: window.location.pathname };
    const { exact = defaults.exact, locationToMatch = defaults.locationToMatch } = config ?? defaults;

    return matchPath(locationToMatch, { path: routeToMatch, exact }) !== null;
};

export const getPortalEnvironmentDomainName = (): PortalEnvironmentDomainName => {
    const { hostname } = window.location;
    if (hostname.includes('staging.language.cognitive.azure') || hostname.includes('7uis')) {
        return 'staging';
    }

    if (hostname.includes('language.cognitive.azure') || hostname.includes('luis')) {
        return 'production';
    }

    return hostname.split('.')[0] as PortalEnvironmentDomainName;
};

export const getServiceNameFromPath = (subPath: string, splittedPath: string[]): Portal['serviceName'] => {
    switch (subPath) {
        case 'luis':
            return 'luis';
        case 'customtext':
            return splittedPath.includes('classification') ? 'classification' : 'ner';
        case 'clu':
            return 'clu';
        case 'orchestration':
            return 'orchestration';
        case 'questionanswering':
            return 'qnaMaker';
        case 'translation':
            return 'translation';
        case 'customhealthcare':
            return 'customHealthcare';
        case 'summarization':
            return 'summarization';
        case 'customsentiment':
            return 'customSentiment';
        default:
            return 'root';
    }
};

export const getPortalFromUrl = (): Portal => {
    const studioUrls = [
        LuisConstants.LanguageStudio.HostName,
        LuisConstants.LanguageStudio.LegacyHostName,
        LuisConstants.LanguageStudio.StagingHostName,
        LuisConstants.LanguageStudio.FairFaxHostName,
        LuisConstants.LanguageStudio.MoonCakeHostName,
        LuisConstants.LanguageStudio.LegacyStagingHostName
    ];
    const splittedPath = window.location.pathname.split('/');
    const subPath = splittedPath[1]?.toLocaleLowerCase();
    let serviceName: Portal['serviceName'];

    if (studioUrls.some(u => u === window.location.hostname)) {
        serviceName = getServiceNameFromPath(subPath, splittedPath);
        switch (subPath) {
            case 'luis':
                return { kind: 'languageStudio', subPath: 'luis', serviceName };
            case 'customtext':
                return {
                    kind: 'languageStudio',
                    subPath: 'customText',
                    serviceName
                };
            case 'clu':
                return { kind: 'languageStudio', subPath: 'clu', serviceName };
            case 'orchestration':
                return { kind: 'languageStudio', subPath: 'orchestration', serviceName };
            case 'questionanswering':
                return { kind: 'languageStudio', subPath: 'qnaMaker', serviceName };
            case 'translation':
                return { kind: 'languageStudio', subPath: 'translation', serviceName };
            case 'customhealthcare':
                return { kind: 'languageStudio', subPath: 'customHealthcare', serviceName };
            case 'summarization':
                return { kind: 'languageStudio', subPath: 'summarization', serviceName };
            case 'customsentiment':
                return { kind: 'languageStudio', subPath: 'customSentiment', serviceName };
            default:
                return { kind: 'languageStudio', subPath: 'root', serviceName };
        }
    }

    if (isDevelopmentEnvironment()) {
        const devSubPath = window.location.pathname.split('/')[2]?.toLocaleLowerCase();
        serviceName = getServiceNameFromPath(devSubPath, splittedPath);
        switch (devSubPath) {
            case 'customtext':
                return {
                    kind: 'languageStudio',
                    subPath: 'customText',
                    serviceName
                };
            case 'clu':
                return { kind: 'languageStudio', subPath: 'clu', serviceName };
            case 'orchestration':
                return { kind: 'languageStudio', subPath: 'orchestration', serviceName };
            case 'questionanswering':
                return { kind: 'languageStudio', subPath: 'qnaMaker', serviceName };
            case 'translation':
                return { kind: 'languageStudio', subPath: 'translation', serviceName };
            case 'customhealthcare':
                return { kind: 'languageStudio', subPath: 'customHealthcare', serviceName };
            case 'summarization':
                return { kind: 'languageStudio', subPath: 'summarization', serviceName };
            case 'customsentiment':
                return { kind: 'languageStudio', subPath: 'customSentiment', serviceName };
            default:
                switch (subPath) {
                    case 'languagestudio':
                        return { kind: 'languageStudio', subPath: 'root', serviceName };
                    case 'demos':
                        return { kind: 'demos' };
                    default:
                        return { kind: 'luis', subPath: 'luis', serviceName };
                }
        }
    }

    return { kind: 'luis', serviceName };
};

export const getLanguageStudioBaseUrl = () =>
    isDevelopmentEnvironment()
        ? window.location.origin
        : isStagingEnvironment()
        ? 'https://staging.language.cognitive.azure.com'
        : 'https://language.cognitive.azure.com';

export const studioLinks = {
    SpeechStudio: (): string => {
        switch (process.env.AzureEnvironment) {
            default:
                return 'https://speech.microsoft.com/portal';
        }
    },
    LanguageStudio: (): string => {
        switch (process.env.AzureEnvironment) {
            default:
                return 'https://language.cognitive.azure.com/';
        }
    },
    CustomTranslator: (): string => {
        switch (process.env.AzureEnvironment) {
            default:
                return 'https://portal.customtranslator.azure.ai/';
        }
    },
    VisionStudio: (): string => {
        switch (process.env.AzureEnvironment) {
            default:
                return 'https://portal.vision.cognitive.azure.com/';
        }
    },
    DocumentIntelligence: (): string => {
        switch (process.env.AzureEnvironment) {
            default:
                return 'https://formrecognizer.appliedai.azure.com/';
        }
    },
    ContentSafety: (): string => {
        switch (process.env.AzureEnvironment) {
            default:
                return 'https://contentsafety.cognitive.azure.com/';
        }
    },
    AzureAIStudio: (): string => {
        switch (process.env.AzureEnvironment) {
            default:
                return 'https://oai.azure.com/portal';
        }
    },
    AzureMachineLearning: (): string => {
        switch (process.env.AzureEnvironment) {
            default:
                return 'https://ml.azure.com/';
        }
    }
};
